import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { axios } from "@/client/libs/axios";
import { useUser } from "@/client/services/user";
import {
  PaginatedWithdrawal,
  paginatedWithdrawalSchema,
  Wallet,
  walletSchema,
  WithdrawalRequest,
} from "../types/wallet";

export const useWalletDashboard = (page = 1) => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const walletQuery = useQuery({
    queryKey: ["user-space", "wallet", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async ({ signal }) => {
      const response = await axios.get<unknown>("/wallets", { signal });
      return walletSchema.parse(response.data);
    },
    staleTime: 10_000,
  });

  const withdrawalsQuery = useQuery({
    queryKey: ["user-space", "withdrawals", user?.id, page],
    enabled: Boolean(user?.id),
    queryFn: async ({ signal }) => {
      const response = await axios.get<unknown>("/withdrawals", {
        params: { page, take: 5 },
        signal,
      });
      return paginatedWithdrawalSchema.parse(response.data);
    },
    staleTime: 10_000,
  });

  const withdrawMutation = useMutation({
    mutationFn: async (data: WithdrawalRequest) => {
      const response = await axios.post("/withdrawals", data);
      return response.data;
    },
    onSuccess: () => {
      toast.success("Demande de retrait envoyée avec succès.");
      void queryClient.invalidateQueries({
        queryKey: ["user-space", "wallet", user?.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["user-space", "withdrawals", user?.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["user-space", "account-overview", user?.id],
      });
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || "Une erreur est survenue lors de la demande.";
      toast.error(message);
    },
  });

  return {
    wallet: walletQuery.data,
    withdrawals: withdrawalsQuery.data,
    loading: walletQuery.isPending || withdrawalsQuery.isPending,
    refreshing: walletQuery.isFetching || withdrawalsQuery.isFetching,
    error: walletQuery.error || withdrawalsQuery.error,
    refetch: () => {
      void walletQuery.refetch();
      void withdrawalsQuery.refetch();
    },
    withdraw: withdrawMutation.mutateAsync,
    isWithdrawing: withdrawMutation.isPending,
  };
};
